import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Edit,
  Delete,
  LocalOffer,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import BranchChip from '../components/BranchChip';
import { useMyLocations } from '../hooks/useMyLocations';
import CouponFormDialog from '../components/CouponFormDialog';

interface Coupon {
  _id: string;
  code: string;
  title: string;
  description?: string;
  discountType: 'percentage' | 'amount';
  discountValue: number;
  maxDiscountAmount?: number;
  applicableType:
    | 'all'
    | 'studio'
    | 'event'
    | 'class'
    | 'class_type'
    | 'all_events'
    | 'all_classes'
    | 'all_packs'
    | 'studio_packs'
    | 'global_packs'
    | 'pack';
  pack?: {
    _id: string;
    title: string;
  };
  isActive: boolean;
  maxUsage?: number;
  usageCount?: number;
  usageLeft?: number;
  minOrderAmount?: number;
  validFrom?: string;
  validUntil?: string;
  expiresAt?: string;
  studio?: {
    _id: string;
    studioName: string;
  };
  event?: {
    _id: string;
    eventName: string;
  };
  class?: {
    _id: string;
    className: string;
  };
  classType?: {
    _id: string;
    title: string;
  };
  createdByStudio?: {
    _id: string;
    studioName: string;
  } | string;
  createdAt: string;
}

const CouponsPage: React.FC = () => {
  const { locations, byId: locationById } = useMyLocations();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [applicableTypeFilter, setApplicableTypeFilter] = useState<'all' | 'studio' | 'event' | 'class' | 'all_events' | 'all_classes'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCoupons = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: any = { pageSize: 1000 }; // fetch all coupons (no server paging)
      if (searchTerm) params.search = searchTerm;
      if (applicableTypeFilter !== 'all') params.applicableType = applicableTypeFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await apiService.getCoupons(params);
      setCoupons(response.coupons || response.data || []);
    } catch (err: any) {
      console.error('Error fetching coupons:', err);
      setError(err.message || 'Failed to fetch coupons');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicableTypeFilter, statusFilter]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    fetchCoupons();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, coupon: Coupon) => {
    setAnchorEl(event.currentTarget);
    setSelectedCoupon(coupon);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Only clear selectedCoupon if we're not opening edit dialog
    // The selectedCoupon will be cleared when edit dialog closes
  };

  const handleEditClick = () => {
    // Close menu first
    setAnchorEl(null);
    // Open edit dialog - selectedCoupon is already set from handleMenuOpen
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedCoupon) return;
    try {
      setDeleting(true);
      // Pinned to the coupon's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deleteCoupon(
        selectedCoupon._id,
        studioIdOf((selectedCoupon as any).createdByStudio) || studioIdOf((selectedCoupon as any).studio),
      );
      await fetchCoupons();
      setDeleteDialogOpen(false);
      setSelectedCoupon(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete coupon');
    } finally {
      setDeleting(false);
    }
  };

  const filteredCoupons = coupons.filter((coupon) => {
    const matchesSearch = 
      coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatDiscountValue = (coupon: Coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}%`;
    }
    return `₹${coupon.discountValue}`;
  };

  const getApplicableToLabel = (coupon: Coupon) => {
    if (coupon.applicableType === 'all') return 'All';
    if (coupon.applicableType === 'all_events') return 'All Events';
    if (coupon.applicableType === 'all_classes') return 'All Classes';
    if (coupon.applicableType === 'all_packs') return 'All Packs';
    if (coupon.applicableType === 'global_packs') return 'SweatSocial Packs';
    if (coupon.applicableType === 'studio_packs') {
      return coupon.studio ? `${coupon.studio.studioName} Packs` : 'Studio Packs';
    }
    if (coupon.applicableType === 'pack') {
      // Multi-pack coupons: name it when there's one, count it when there are
      // many — a list of 9 pack titles would blow out the column.
      const list: any[] = (coupon as any).packs || [];
      if (list.length > 1) return `${list.length} packs`;
      return list[0]?.title || (coupon as any).pack?.title || 'Pack';
    }
    if (coupon.applicableType === 'studio' && coupon.studio) return coupon.studio.studioName;
    if (coupon.applicableType === 'event' && coupon.event) return coupon.event.eventName;
    if (coupon.applicableType === 'class' && coupon.class) return coupon.class.className;
    if (coupon.applicableType === 'class_type' && coupon.classType) return `All ${coupon.classType.title}`;
    return coupon.applicableType;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700} color={colors.textPrimary}>
          Coupons
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: colors.primary,
            '&:hover': {
              backgroundColor: colors.primaryDark,
            },
          }}
        >
          Create Coupon
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ border: `1px solid ${colors.divider}` }}>
        <CardContent>
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Search coupons by code or title..."
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearchSubmit}>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background,
                },
              }}
            />
          </Box>

          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <Box display="flex" gap={1}>
              <Button
                variant={applicableTypeFilter === 'all' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setApplicableTypeFilter('all')}
              >
                All
              </Button>
              <Button
                variant={applicableTypeFilter === 'studio' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setApplicableTypeFilter('studio')}
              >
                Studio
              </Button>
              <Button
                variant={applicableTypeFilter === 'event' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setApplicableTypeFilter('event')}
              >
                Event
              </Button>
              <Button
                variant={applicableTypeFilter === 'class' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setApplicableTypeFilter('class')}
              >
                Class
              </Button>
              <Button
                variant={applicableTypeFilter === 'all_events' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setApplicableTypeFilter('all_events')}
              >
                All Events
              </Button>
              <Button
                variant={applicableTypeFilter === 'all_classes' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setApplicableTypeFilter('all_classes')}
              >
                All Classes
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box display="flex" gap={1}>
              <Button
                variant={statusFilter === 'all' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setStatusFilter('all')}
              >
                All Status
              </Button>
              <Button
                variant={statusFilter === 'active' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setStatusFilter('active')}
              >
                Active
              </Button>
              <Button
                variant={statusFilter === 'inactive' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setStatusFilter('inactive')}
              >
                Inactive
              </Button>
            </Box>
          </Box>

          {filteredCoupons.length === 0 ? (
            <Box textAlign="center" py={4}>
              <LocalOffer sx={{ fontSize: 64, color: colors.textSecondary, mb: 2 }} />
              <Typography variant="h6" color={colors.textSecondary}>
                {searchTerm ? 'No coupons found' : 'No coupons yet'}
              </Typography>
              <Typography variant="body2" color={colors.textSecondary} mt={1}>
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Create your first coupon to get started'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.background }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Applicable To</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usage</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCoupons.map((coupon) => (
                    <TableRow key={coupon._id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight={600}>
                          {coupon.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {coupon.title}
                        </Typography>
                        {coupon.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {coupon.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDiscountValue(coupon)}
                        </Typography>
                        {coupon.maxDiscountAmount && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Max: ₹{coupon.maxDiscountAmount}
                          </Typography>
                        )}
                        {coupon.minOrderAmount && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Min: ₹{coupon.minOrderAmount}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getApplicableToLabel(coupon)}
                          size="small"
                          sx={{
                            backgroundColor: `${colors.primary}15`,
                            color: colors.primary,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {coupon.createdByStudio && locations.length > 1 ? (
                          // A brand with several locations: the same location
                          // bubble as every other page — one per location it
                          // works at (its own first).
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {[studioIdOf(coupon.createdByStudio), ...((coupon as any).applicableStudios ?? []).map(studioIdOf)]
                              .filter((id, i, all) => id && all.indexOf(id) === i)
                              .map((id) => (
                                <BranchChip
                                  key={id}
                                  location={
                                    locationById(id) ??
                                    (id === studioIdOf(coupon.createdByStudio)
                                      ? ({ _id: id, studioName: (coupon.createdByStudio as any)?.studioName } as any)
                                      : null)
                                  }
                                />
                              ))}
                          </Box>
                        ) : coupon.createdByStudio ? (
                          <Chip
                            label={
                              typeof coupon.createdByStudio === 'object' &&
                              coupon.createdByStudio !== null
                                ? coupon.createdByStudio.studioName
                                : 'Studio'
                            }
                            size="small"
                            sx={{
                              backgroundColor: `${colors.info}15`,
                              color: colors.info,
                              fontWeight: 500,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Admin"
                            size="small"
                            sx={{
                              backgroundColor: `${colors.secondary}15`,
                              color: colors.secondary,
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={coupon.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: coupon.isActive
                              ? `${colors.success}15`
                              : `${colors.textSecondary}15`,
                            color: coupon.isActive
                              ? colors.success
                              : colors.textSecondary,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {coupon.maxUsage != null && coupon.maxUsage > 0 ? (
                          <Typography variant="body2">
                            {coupon.usageCount || 0} / {coupon.maxUsage}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unlimited
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {coupon.expiresAt || coupon.validUntil ? (
                          <Typography variant="body2">
                            {new Date(Math.min(...[coupon.expiresAt, coupon.validUntil].filter((d): d is string => !!d).map((d) => new Date(d).getTime()))).toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No expiry
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, coupon)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick();
          }}
        >
          <Edit sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }} 
          sx={{ color: colors.error }}
        >
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      <CouponFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          fetchCoupons();
          setCreateDialogOpen(false);
        }}
      />

      {selectedCoupon && (
        <>
          <CouponFormDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedCoupon(null);
            }}
            onSuccess={() => {
              fetchCoupons();
              setEditDialogOpen(false);
              setSelectedCoupon(null);
            }}
            coupon={selectedCoupon}
          />

          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: deleteDialogOpen ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => setDeleteDialogOpen(false)}
          >
            <Card
              sx={{ p: 3, maxWidth: 400, width: '90%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h6" fontWeight={600} mb={2}>
                Delete Coupon
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Are you sure you want to delete the coupon "{selectedCoupon.code}"? This action cannot be undone.
              </Typography>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting}
                  startIcon={deleting && <CircularProgress size={16} />}
                >
                  Delete
                </Button>
              </Box>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CouponsPage;

